// React
import React, { useState } from "react";
// Components
import { ToastQueueProvider } from "../ToastQueueProvider/ToastQueueProvider";
import ValidationButton from "../validationButton/ValidationButton";
import CodeEditor from "./CodeEditor";
// React Bootstrap
import { Card, Form, Accordion } from "react-bootstrap";
// Styles
import styles from "./CodeEditor.module.css";
// Fhir Front Library
import { FhirStatus, StatusAlert } from "@fyrstain/hl7-front-library";
// FontAwesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faXmark } from "@fortawesome/free-solid-svg-icons";
// Translation
import i18n from "i18next";

////////////////////////////////
//           Props            //
////////////////////////////////

interface PanelCardProps {
  // The title of the panel
  title: string;
  // The content of the panel
  content: Array<inputs>;
  // The function to update the content
  setContent: any;
  // To disable the "check" button when the user has an error
  isFailingOperation?: boolean;
  // The context type for the panel
  contextType?: "Questionnaire" | "StructureMap";
  // To set the text area as read only
  readOnly?: boolean;
}

////////////////////////////////
//         Interfaces         //
////////////////////////////////

interface inputs {
  name: string;
  type?: string;
  content?: string;
  profile?: string;
  validationReportID?: string;
  validationReportStatus?: string;
}

interface ShowAlertType {
  [key: number]: boolean;
}

const PanelCard: React.FC<PanelCardProps> = ({
  title,
  content,
  setContent,
  isFailingOperation,
  contextType,
  readOnly,
}) => {
  /////////////////////////////////////
  //      Constants / ValueSet       //
  /////////////////////////////////////

  const [showAlert, setShowAlert] = useState<ShowAlertType>({});

  // Identifies the card currently being validated.
  const [, setIsDataLoading] = useState<number | null>(null);

  // Creating a Toast using the Component Toast
  const { createToast } = ToastQueueProvider.useToastQueue();

  ////////////////////////////////
  //           Actions          //
  ////////////////////////////////

  /**
   * Open the only one alert of the card and update the state
   * @param index ID/position of an alert in the list
   */
  const handleOpenAlert = (index: number) => {
    setShowAlert({
      ...showAlert,
      [index]: true,
    });
  };

  /**
   * Close the alert of the card
   * @param index ID/position of an alert in the list
   */
  const handleCloseAlert = (index: number) => {
    setShowAlert({
      ...showAlert,
      [index]: false,
    });
  };

  /**
   * Capitalize the first letter
   *
   * @param string the string to capitalize
   */
  function capitalizeFirst(string: string): string {
    const splitted = string.split("");
    splitted[0] = splitted[0].toUpperCase();
    return splitted.join("");
  }

  function download(entry: inputs) {
    if (!entry.content) return;
    const isCsv = entry.type?.toUpperCase() === "CSV";
    const blob = new Blob([entry.content], {
      type: isCsv ? "text/csv" : "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${entry.name}.${isCsv ? "csv" : "json"}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <div className="accordionCard">
      <Form.Group className="col">
        {content.length === 0 && (
          <Card.Body className={styles.cardPanelPadding}>
            <Form.Control
              as="textarea"
              rows={22}
              name={title + "-empty"}
              readOnly={true}
              value={i18n.t("text.paneltext")}
              onChange={(value: any) => setContent(value)}
              className={styles.textAreaPanel}
            />
          </Card.Body>
        )}
        {content.map((entry, index) => {
          return (
            <Accordion defaultActiveKey="0" key={index}>
              <Accordion.Item eventKey="0">
                {entry.content && (
                  <button
                    type="button"
                    aria-label={`Download ${entry.name}`}
                    onClick={() => download(entry)}
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                )}
                <Accordion.Header>
                  {entry.content && entry.content.trim() !== "" && (
                    <ValidationButton
                      entry={entry}
                      index={index}
                      onValidated={() => setContent([...content])}
                      onOpenAlert={handleOpenAlert}
                      setIsDataLoading={setIsDataLoading}
                      createToast={createToast}
                      isDisabled={isFailingOperation}
                      contextType={contextType}
                    />
                  )}
                  <div className="textTruncate">{entry.name.toUpperCase()}</div>
                </Accordion.Header>
                <Accordion.Body>
                  {showAlert[index] && (
                    <StatusAlert
                      flavor={FhirStatus[(entry.validationReportStatus || "unknown") as keyof typeof FhirStatus]}
                    >
                      <div className={styles.alertContainer}>
                        <div>
                          <strong className={styles.alertTitle}>
                            {`${capitalizeFirst(entry.validationReportStatus || "Unknown error")} validation`}
                          </strong>
                          <div>
                            <a
                              href={`${process.env.REACT_APP_VALIDATION_REPORT_URL}/${entry.validationReportID}`}
                              target="_blank"
                              rel="noreferrer"
                              className={styles.link}
                            >
                              {entry.validationReportID ? i18n.t("text.validationreport") : ""}
                            </a>
                          </div>
                        </div>
                        <div className={styles.xmarkEnd}>
                          <FontAwesomeIcon
                            icon={faXmark}
                            onClick={() => handleCloseAlert(index)}
                            className="actionIcon"
                          />
                        </div>
                      </div>
                    </StatusAlert>
                  )}
                  {readOnly ? (
                    <Form.Control
                      as="textarea"
                      rows={22}
                      name={title + "-" + index}
                      readOnly={true}
                      value={entry.content ? entry.content : i18n.t("text.paneltext")}
                      className={styles.textAreaPanel}
                    />
                  ) : (
                    <CodeEditor
                      content={entry.content ?? ""}
                      setContent={(value: any) => {
                        const updated = [...content];
                        updated[index] = { ...entry, content: value };
                        setContent(updated);
                      }}
                    />
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          );
        })}
      </Form.Group>
    </div>
  );
};

export default PanelCard;
